import { rdbV1FiltersYearsList } from '@on3/api';
import type { ICommitsIndexProps } from '@on3/ui-lib/src/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { capitalizeFirstLetter } from '@on3/ui-lib/utils/capitalizeFirstLetter';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { CommitList } from 'components/College/Interests/Commits/CommitList';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

const CommitsIndex = (props: ICommitsIndexProps) => {
  const { currentSite } = props?.siteData || {};

  const router = useRouter();
  const { year, sport, status } = router.query;
  const capitilzedSport = capitalizeFirstLetter(`${sport}`);
  const orgName =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;
  const orgMascot = currentSite?.organization?.mascot;
  const title = `${year} ${orgName} ${orgMascot} ${capitilzedSport} Commits`;
  const description = `${year} ${orgName} ${orgMascot} ${capitilzedSport} team commit list.`;
  const sortedList = status
    ? props?.commitList?.list?.filter(
        (item) =>
          item.status?.type?.toLowerCase() === `${status}` &&
          !item.status.transferredAsset,
      )
    : props?.commitList?.list;
  const noIndex =
    props?.commitList?.pagination?.count === 0 || sortedList?.length === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <CommitList
        {...props}
        key={`${year}-${sport}-${orgName}-${status}`}
        title={title}
        toggled={false}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  // Check and validate year
  // const validYears = ['2000','2025'];

  const validInterestTypes = ['commits', 'visits', 'offers'];

  const orgKey = siteData?.currentSite?.organizationKey;
  const { year, sport } = ctx.query;

  // validation checks
  // TODO: Validate year is in range.

  const { rankingSports } = siteData || {};

  const sportKey = rankingSports?.find(
    (sp) => sp?.name && slugify(sp?.name) === sport,
  )?.key;

  if (!orgKey || !year || !sport || !sportKey) {
    return {
      notFound: true,
    };
  }

  // once we see what the other endpoints look like, we can make the following more dynamic
  try {
    const [commitList, teamRank, teams, years, conferences] =
      await Promise.allSettled([
        proxyApi
          .get(
            `/public/rdb/v2/organizations/${orgKey}/commits?sportKey=${sportKey}&year=${year}`,
          )
          .then((r) => r.data),
        proxyApi
          .get(
            `/public/rdb/v1/team-ranking/organizations/${orgKey}/summary?sportKey=${sportKey}&year=${year}`,
          )
          .then((r) => r.data),
        proxyApi
          .get(
            `/public/rdb/v1/filters/teams?groupBy=true&sportKey=${sportKey}&year=${year}`,
          )
          .then((r) => r.data),
        rdbV1FiltersYearsList(proxyApi),
        proxyApi.get(`/public/rdb/v1/filters/conferences`).then((r) => r.data),
      ]);

    if (commitList.status === 'rejected') {
      return {
        notFound: true,
      };
    }

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      commitList: commitList.status === 'fulfilled' ? commitList.value : [],
      teamRank: teamRank.status === 'fulfilled' ? teamRank.value : null,
      teams: teams.status === 'fulfilled' ? teams.value : [],
      years: years.status === 'fulfilled' ? years.value : [],
      conferences: conferences.status === 'fulfilled' ? conferences.value : [],
      sports: rankingSports,
      status: validInterestTypes,
    };

    // TODO: if interests 404s, we need to 404 instead of trying to render the page.

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(CommitsIndex);
